import { useLayoutEffect, useRef, type RefObject } from "react";

/**
 * One sliding active-tab indicator (NV1 — the approved navbar-1/navbar-7
 * grammar): the row owns a single absolutely-positioned mark and moves it to
 * the active member by measurement, instead of each tab painting its own.
 * CSS transitions on left/width carry the slide; the caller's
 * `motion-reduce:transition-none` class silences it. The container must be
 * the mark's offset parent (position: relative) so member offsets read
 * directly. Measurement re-runs when the active key changes and when the
 * container or any member resizes; with no active member the mark hides
 * rather than pointing at nothing.
 */
export function useSlidingTabIndicator(
  listRef: RefObject<HTMLElement | null>,
  activeKey: string | null,
  activeSelector: string,
): RefObject<HTMLSpanElement | null> {
  const indicatorRef = useRef<HTMLSpanElement | null>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (list === null || indicator === null) return;
    const measure = () => {
      const active = list.querySelector<HTMLElement>(activeSelector);
      if (active === null) {
        indicator.style.opacity = "0";
        return;
      }
      indicator.style.opacity = "1";
      indicator.style.left = `${active.offsetLeft}px`;
      indicator.style.width = `${active.offsetWidth}px`;
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (observer !== null) {
      observer.observe(list);
      for (const member of list.querySelectorAll<HTMLElement>('[role="tab"], [data-world-door]')) {
        observer.observe(member);
      }
    }
    return () => observer?.disconnect();
  }, [activeKey, activeSelector, listRef]);
  return indicatorRef;
}
